import * as React from "react";
import { Form, Spinner } from "react-bootstrap";
import Select, { Props } from "react-select";
import { shallowEqual } from "react-redux";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import { OrganizationConsent, OrganizationType } from "../../Types/Common/Types";
import { setTrialLabelsOnMeasurement } from "../../libs/trialsLib";
import { setOrganizationConsent } from "../../redux/patient/actionCreators";
import { MeasurementData } from "../../Types/Common/MeasurementTypes";

const { useState, useEffect, useRef } = React;

interface MeasurementTrialLabelsDropdownProps {
  organization: OrganizationType;
  measurement: Pick<MeasurementData, 'measurementID' | 'labels' | 'patientID'>;
  setActiveMeasurement?: React.Dispatch<React.SetStateAction<MeasurementData | undefined>>;
  updateLocalMeasurement: (updatedMeasurement: MeasurementData) => void;
  reloadParticipantDetailsAtHomeStartDate: () => Promise<void>;
};

export const MeasurementTrialLabelsDropdown: React.FC<MeasurementTrialLabelsDropdownProps> = ({ organization, measurement, setActiveMeasurement, updateLocalMeasurement, reloadParticipantDetailsAtHomeStartDate }) => {
  const dispatch = useAppDispatch();
  const authenticatedProvider = useAppSelector(state => state.provider, shallowEqual);
  const program = useAppSelector(state => state.programs[organization.program?.toUpperCase()]);
  const isReadOnly = authenticatedProvider.viewingAs!.readOnly || (!!program?.featureSet?.enableKioskMandatoryVisitLabels && !authenticatedProvider?.viewingAs?.orgAdmin && !authenticatedProvider.fullAdmin) || !!program?.visitTemplate;
  const { organizationConsent } = useAppSelector(state => state.patient);
  const isOrganizationConsentFallback = organizationConsent.organizationID && organizationConsent.organizationID !== organization.organizationID;

  const [isLoading, setLoading] = useState(false);
  const [trialLabelsOptions, setTrialLabelsOptions] = useState<{ value: string; label: string; }[]>(
    measurement?.labels?.[organization.organizationID]
      ? measurement?.labels[organization.organizationID]?.map(label => ({ value: label, label })) ?? []
      : []
  );
  const previousTrialLabelsOptions = useRef(trialLabelsOptions);
  const [error, setError] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  const formatTrialLabelsForProgram = (labels: string[]): any => {
    return labels.map(label => {
      return { value: label, label: label };
    });
  };
  const trialLabelsForProgram = formatTrialLabelsForProgram(program?.trialMode?.labels ?? []);

  const handleChangeTrialLabelOption: Props<typeof trialLabelsOptions, true>['onChange'] = (e) => {
    if (isReadOnly || !e) return;
    previousTrialLabelsOptions.current = trialLabelsOptions;
    setTrialLabelsOptions(e as unknown as typeof e[number]);
  };

  useEffect(() => {
    const updateMeasurementTrialLabels = async (trialLabelsValues: string[]) => {
      if (isLoading || !measurement) return;
      setLoading(true);
      setError('');
      setMessage('');

      try {
        const { consent: partialUpdatedConsent, measurement: partialUpdatedMeasurement, message, needsDatesRefresh } = await setTrialLabelsOnMeasurement(measurement.patientID, organization.organizationID, measurement.measurementID, program.program, trialLabelsValues, organizationConsent.labels, measurement.labels);
        const updatedMeasurement: MeasurementData = { ...measurement, ...partialUpdatedMeasurement };
        // await updateSessionData(updatedMeasurement);
        const updatedConsent: OrganizationConsent = { ...organizationConsent, ...partialUpdatedConsent };
        dispatch(setOrganizationConsent(updatedConsent));
        setMessage(message);
        // const trialLabelsSeriesOptions = { measurementID: measurement.measurementID, option: trialLabelsValues };
        if (setActiveMeasurement) setActiveMeasurement(updatedMeasurement);
        updateLocalMeasurement(updatedMeasurement);
        if (needsDatesRefresh) {
          console.log('needs dates refresh')
          reloadParticipantDetailsAtHomeStartDate();
        }
      } catch (e) {
        console.log(e);
        const message: unknown = (e as any)?.response?.data?.message;
        if (typeof message === 'string') {
          setError(message);
        } else {
          setError("Could not update labels - please refresh your page and try again");
        }
        setTrialLabelsOptions(previousTrialLabelsOptions.current);
      } finally {
        setLoading(false);
      };
    };

    // So so sorry, I don't know enough React to know how to use a useMemo (perhaps?) in this situation,
    // but I do know enough to know that this is suboptimal.
    // Probably even something like this would be better: https://stackoverflow.com/a/55614659.
    const selectedValues = trialLabelsOptions.map(o => o.value);
    const stored = measurement?.labels?.[organization.organizationID] ?? [];
    const needsUpdate = JSON.stringify([...selectedValues].sort()) !== JSON.stringify([...stored].sort());
    if (needsUpdate) updateMeasurementTrialLabels(selectedValues);

  }, [measurement, setActiveMeasurement, trialLabelsOptions, updateLocalMeasurement, dispatch, organization.organizationID, organizationConsent, program.program]);

  if (!organizationConsent.organizationID) {
    return <Spinner animation="grow" variant="primary"></Spinner>;
  }
  // I've always found behavior confusing when viewingAs an org the patient is not consented to.
  // (i.e. for trend notifications, how do you know which org the fallback org consent is for?)
  // To avoid ambiguity, just don't render anything if so.
  if (isOrganizationConsentFallback) {
    return <p className="text-warning font-weight-bold">Labels can only be viewed when "Viewing As" an organization this patient is consented to.</p>;
  }
  return (
    <Form className="w-100" inline={true}>
      <div className="w-100" data-testid={`testid-${trialLabelsOptions?.map(i => i.value).join("-")}`}>
        <Select
          id={`id-${trialLabelsOptions?.map(i => i.value).join("-")}`}
          className="form-select form-select-inline"
          isSearchable={false}
          isMulti
          value={trialLabelsOptions}
          onChange={handleChangeTrialLabelOption}
          options={trialLabelsForProgram}
          isDisabled={isReadOnly || isLoading}
          isLoading={isLoading}
        />
        {!!error && <p className='text-danger'>{error}</p>}
        {!!message && <p className='text-success'>{message}</p>}
      </div>
    </Form>
  );
};